import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { Button, LinkButton } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { ExternalLink } from '@/components/base/link/link';
import { BASE_PATH } from '@/constants/config';
import { useCaptureParticipantUtmFromSearch } from '@/hooks/use-capture-participant-utm-from-search';
import {
  isRecaptchaV3LoadTimeoutError,
  RecaptchaScript,
  RecaptchaV2Challenge,
  useRecaptcha,
} from '@/hooks/use-recaptcha';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { useSignupStartedConversion } from '@/lib/analytics/use-signup-started-conversion';
import { syncableProfileCookieSettings } from '@/lib/consent/consent-profile';
import { useConsent } from '@/lib/consent/use-consent';
import { AccountExistsNotice } from '@/routes/-auth/account-exists-notice';
import {
  readUtmHiddenField,
  UtmHiddenField,
} from '@/routes/-auth/utm-hidden-field';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { SignupCarousel } from '@/routes/participants/-components/signup-carousel';
import { toInitialUtm, utmSearchSchema } from '@/schemas/utm';
import { detectTimezone } from '@/utils/detect-timezone';
import { getSafeReturnTo, storeReturnTo } from '@/utils/get-safe-return-to';
import {
  clearParticipantUtmStorage,
  getParticipantUtmSearchForRouter,
} from '@/utils/participant-utm-storage';
import {
  clearProjectReferralAttribution,
  resolveSignupReferralAttribution,
} from '@/utils/referral-attribution';
import { ERROR_CODES, signupCompletedDedupId } from '@common/utils';

/**
 * The two failures the page renders differently: one has somewhere to send the
 * user, the other only has something to tell them. Modelled as one value so a
 * submit cannot leave both showing at once.
 */
type SignupFailure =
  | { reason: 'accountExists'; email: string }
  | { reason: 'unresolvable'; message: string };

const signupSearchSchema = utmSearchSchema.extend({
  /** Legacy network referral param; same meaning as `referralCode` on project links. */
  r: z.string().optional(),
  referralCode: z.string().optional(),
  projectId: z.string().optional(),
  returnTo: z.string().optional(),
  leadId: z.string().optional(),
});

export const Route = createFileRoute('/participants/signup')({
  validateSearch: signupSearchSchema,
  beforeLoad: ({ context, search }) => {
    // Root-context session, not an independent `getServerSession()` — see the
    // note in `participants/login.tsx`.
    if (context.user) {
      const safePath = getSafeReturnTo(search.returnTo);
      if (safePath) {
        throw redirect({ href: safePath });
      }
      throw redirect({ to: '/participants' });
    }
  },
  component: SignupPage,
});

function SignupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const analytics = useAnalytics();
  // Seeds the new profile with the decision this browser already holds, so
  // signing up doesn't silently reset the visitor's choice.
  const { stored: storedConsent } = useConsent();
  const signupCookieSettings = syncableProfileCookieSettings(storedConsent);
  const [failure, setFailure] = useState<SignupFailure | null>(null);
  const [showV2Challenge, setShowV2Challenge] = useState(false);
  const [v2Token, setV2Token] = useState<string | null>(null);
  const { getToken } = useRecaptcha();
  const recaptchaResetRef = useRef<(() => void) | null>(null);

  useCaptureParticipantUtmFromSearch(search);

  const screenedRef = useRef(false);
  useEffect(() => {
    if (!analytics || screenedRef.current) {
      return;
    }
    screenedRef.current = true;
    void (async () => {
      const { referralCode } = resolveSignupReferralAttribution(search);
      analytics.screen({
        name: 'signup',
        attributes: {
          signupMethod: 'email',
          referralCode: referralCode ?? null,
          utmSource: search.utm_source ?? null,
        },
      });
    })();
  }, [analytics, search]);

  // Email is the only way into an account from this page; the reported method
  // is derived from that list rather than named here, so adding a social
  // button cannot leave the funnel counting its starts as email starts.
  useSignupStartedConversion({
    methods: ['email'],
    attributes: {
      referralCode:
        resolveSignupReferralAttribution(search).referralCode ?? null,
      utmSource: search.utm_source ?? null,
    },
  });

  const signupFormSchema = z.object({
    firstName: z
      .string()
      .trim()
      .min(1, t`First name is required`),
    lastName: z
      .string()
      .trim()
      .min(1, t`Last name is required`),
    email: z
      .email(t`Please enter a valid email address`)
      .trim()
      .min(1, t`Email is required`),
    password: z.string().min(8, t`Password must be at least 8 characters`),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: t`You must accept the Terms and Conditions`,
    }),
  });

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      agreeToTerms: false,
    },
    validators: {
      onSubmit: signupFormSchema,
      onChange: signupFormSchema,
    },
    onSubmit: async ({ value }) => {
      setFailure(null);

      try {
        const captchaToken = v2Token ? 'v2-fallback' : await getToken('signup');
        const { referralCode, referrerProjectId } =
          resolveSignupReferralAttribution(search);
        // URL, then this app's own 30-day store, then whatever the tag
        // container carried over. Resolved here rather than at render because
        // the container fills its field after the page mounts.
        const initialUtm = toInitialUtm({
          ...readUtmHiddenField('participant'),
          ...getParticipantUtmSearchForRouter(search),
        });

        const response = await fetch(
          `${BASE_PATH}/api/auth/participant-signup`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              firstName: value.firstName.trim(),
              lastName: value.lastName.trim(),
              email: value.email.trim(),
              password: value.password,
              termsAccepted: value.agreeToTerms,
              leadId: search.leadId,
              captchaToken,
              ...(v2Token && { captchaV2Token: v2Token }),
              ...(initialUtm && { initialUtm }),
              ...(referralCode && { referralCode }),
              ...(referrerProjectId && { referrerProjectId }),
              // A decision this browser recorded, or nothing. The helper drops
              // a record that is not syncable, and carries the categories the
              // visitor left unanswered as absent fields rather than as
              // denials — the endpoint takes a partial record for exactly that
              // reason.
              ...(signupCookieSettings && {
                cookieSettings: signupCookieSettings,
              }),
              ...(detectTimezone() && { timezone: detectTimezone() }),
            }),
            credentials: 'include',
          },
        );

        const data: {
          error?: string;
          message?: string;
          user?: { id?: string };
        } = await response.json();

        if (response.ok) {
          clearProjectReferralAttribution();
          clearParticipantUtmStorage();

          storeReturnTo(search.returnTo);

          analytics?.track({
            action: 'completed',
            actionSubject: 'signup',
            attributes: {
              method: 'email',
              referralCode: referralCode ?? null,
              hasReferral: !!referralCode,
            },
          });
          // The dedup id is derived from the account this request created, so
          // this and core-api's report of the same signup collapse into one
          // conversion. Skipped rather than emitted unpaired if the id is
          // somehow absent: core-api reports it regardless, and an unpaired
          // browser event would be counted a second time.
          //
          // `immediate` because the navigation below reloads the document:
          // a batched conversion would be lost with the page.
          if (data.user?.id) {
            analytics?.conversion(
              {
                name: 'signup_completed',
                eventId: signupCompletedDedupId(data.user.id),
                attributes: {
                  method: 'email',
                  hasReferral: !!referralCode,
                },
              },
              { immediate: true },
            );
          }
          void navigate({
            to: '/participants/verify-email',
            reloadDocument: true,
          });
        } else if (data.error === ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED) {
          setShowV2Challenge(true);
          setFailure(null);
        } else if (data.error === ERROR_CODES.ACCOUNT_EXISTS) {
          if (v2Token) {
            setV2Token(null);
            recaptchaResetRef.current?.();
          }
          setFailure({ reason: 'accountExists', email: value.email.trim() });
          analytics?.ui({
            action: 'viewed',
            actionSubject: 'signupAccountExistsNotice',
            actionSubjectId: 'participant',
            source: 'signupPage',
          });
        } else if (data.error === ERROR_CODES.EMAIL_DOMAIN_NOT_ALLOWED) {
          setFailure({
            reason: 'unresolvable',
            message: t`We can't accept email addresses from this provider. Please use a different email address.`,
          });
          if (v2Token) {
            setV2Token(null);
            recaptchaResetRef.current?.();
          }
        } else {
          setFailure({
            reason: 'unresolvable',
            message: data.message || t`Signup failed`,
          });
          if (v2Token) {
            setV2Token(null);
            recaptchaResetRef.current?.();
          }
        }
      } catch (error) {
        if (isRecaptchaV3LoadTimeoutError(error)) {
          setShowV2Challenge(true);
          setFailure(null);
        } else {
          console.error('[signup] submit failed', error);
          setFailure({
            reason: 'unresolvable',
            message: t`An unexpected error occurred`,
          });
          if (v2Token) {
            setV2Token(null);
            recaptchaResetRef.current?.();
          }
        }
      }
    },
  });

  return (
    <AuthLayout rightPanel={<SignupCarousel />}>
      <RecaptchaScript />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <UtmHiddenField side="participant" />

        {/* Header */}
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Sign up to participate</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              Get paid for sharing valuable insights and help shape world-class
              products.
            </Trans>
          </p>
        </div>

        {/* Form fields and actions */}
        <div className="flex flex-col gap-6">
          {/* Form fields */}
          <div className="flex flex-col gap-5">
            {/* Name row */}
            <div className="flex gap-2.5">
              <div
                className="w-full"
                data-testid="participant-signup-first-name-input"
              >
                <FormInput
                  type="text"
                  form={form}
                  field="firstName"
                  label={t`First name`}
                  placeholder={t`Enter first name`}
                  isRequired
                  size="md"
                />
              </div>
              <div
                className="w-full"
                data-testid="participant-signup-last-name-input"
              >
                <FormInput
                  type="text"
                  form={form}
                  field="lastName"
                  label={t`Last name`}
                  placeholder={t`Enter last name`}
                  isRequired
                  size="md"
                />
              </div>
            </div>

            <div data-testid="participant-signup-email-input">
              <FormInput
                type="email"
                form={form}
                field="email"
                label={t`Email`}
                placeholder={t`Enter your email`}
                isRequired
                size="md"
              />
            </div>

            <div data-testid="participant-signup-password-input">
              <FormInput
                type="password"
                form={form}
                field="password"
                label={t`Password`}
                placeholder={t`Create a password`}
                hint={t`Must be at least 8 characters.`}
                isRequired
                size="md"
              />
            </div>

            <div data-testid="participant-signup-accept-policy">
              <FormInput
                form={form}
                field="agreeToTerms"
                type="checkbox"
                size="md"
                isRequired
                label={
                  <span className="text-sm text-primary">
                    <Trans>
                      I agree to Respondent&apos;s{' '}
                      <ExternalLink
                        href="https://www.respondent.io/participant-terms-conditions"
                        target="_blank"
                        className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
                      >
                        Terms and conditions
                      </ExternalLink>{' '}
                      and{' '}
                      <ExternalLink
                        href="https://www.respondent.io/privacy-policy"
                        target="_blank"
                        className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
                      >
                        Privacy Policy
                      </ExternalLink>
                    </Trans>
                  </span>
                }
              />
            </div>
          </div>

          {/* reCAPTCHA v2 fallback challenge */}
          {showV2Challenge && (
            <RecaptchaV2Challenge
              onVerify={(token) => {
                setV2Token(token);
              }}
              onExpire={() => {
                setV2Token(null);
              }}
              resetRef={recaptchaResetRef}
            />
          )}

          {failure?.reason === 'accountExists' && (
            <AccountExistsNotice
              email={failure.email}
              returnTo={search.returnTo}
            />
          )}

          {failure?.reason === 'unresolvable' && (
            <p className="text-sm text-error-primary">{failure.message}</p>
          )}

          {/* Continue button */}
          <form.Subscribe
            selector={(state) => ({
              isSubmitting: state.isSubmitting,
            })}
            children={({ isSubmitting }) => (
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting}
                isDisabled={isSubmitting || (showV2Challenge && !v2Token)}
                className="w-full"
                data-testid="participant-signup-continue-button"
                onClick={() => {
                  analytics?.ui({
                    action: 'clicked',
                    actionSubject: 'signupMethodButton',
                    actionSubjectId: 'email',
                    source: 'signupPage',
                    attributes: { method: 'email' },
                  });
                }}
              >
                <Trans>Continue</Trans>
              </Button>
            )}
          />
        </div>

        <ul className="flex flex-col items-start gap-1">
          <li className="text-sm text-tertiary">
            <Trans>Already have a participant account?</Trans>{' '}
            <LinkButton
              color="link-color"
              size="md"
              to="/participants/login"
              search={{ returnTo: search.returnTo }}
              data-testid="participant-signup-login-link"
            >
              <Trans>Log in</Trans>
            </LinkButton>
          </li>

          <li className="text-sm text-tertiary">
            <Trans>Not a participant?</Trans>{' '}
            <LinkButton
              color="link-color"
              size="md"
              to="/researchers/signup"
              data-testid="participant-signup-researcher-link"
            >
              <Trans>Sign up as a researcher</Trans>
            </LinkButton>
          </li>
        </ul>
      </form>
    </AuthLayout>
  );
}
